import { useCallback, useMemo } from "react";

import { useGetTasksQuery } from "@/store/api/tasks";

interface UseTasksParams {
  appUserId: string;
  isPatient?: boolean;
  linkedPatients?: LinkedPatientProfile[];
}

interface UseTasks {
  appUserCount: number;
  isLoading: boolean;
  patientsCounters: Record<string, number>;
  refetchTasks: () => Promise<void>;
  tasks: Record<string, TaskItem[]>;
}

const useTasks = ({
  appUserId,
  isPatient,
  linkedPatients = [],
}: UseTasksParams): UseTasks => {
  const {
    data: tasks = {},
    isLoading,
    refetch: refetchTasksQuery,
  } = useGetTasksQuery({
    appUserId,
    isPatient,
    linkedPatients,
  });

  const refetchTasks = useCallback(async () => {
    await refetchTasksQuery();
  }, [refetchTasksQuery]);

  const appUserCount = useMemo(() => {
    const appUserTasks = tasks[appUserId] ?? [];
    return appUserTasks.length;
  }, [tasks, appUserId]);

  const patientsCounters = useMemo(() => {
    const counters: Record<string, number> = {};
    linkedPatients.forEach((patient) => {
      const patientTasks = tasks[patient.externalId] ?? [];
      counters[patient.externalId] = patientTasks.length;
    });
    return counters;
  }, [tasks, linkedPatients]);

  return {
    appUserCount,
    isLoading,
    patientsCounters,
    refetchTasks,
    tasks,
  };
};

export type { UseTasks };
export default useTasks;
